import { FormattedMessage } from 'react-intl';
import mixpanel from 'mixpanel-browser';

// ASSETS
import PlusIcon from 'assets/PlusIcon';

// COMPONENTS
import Loading from 'components/Loading';

// STYLES
import {
  PrimaryButton as PrimaryButtonStyle,
  SecondaryButton as SecondaryButtonStyle,
  SuccessButton as SuccessButtonStyle,
  DangerButton as DangerButtonStyle,
  LightDangerButton as LightDangerButtonStyle,
  LightTableButton as LightTableButtonStyle,
  PrimaryTableButton as PrimaryTableButtonStyle,
  LinkButton as LinkButtonStyle,
  DisabledButton as DisabledButtonStyle,
  FlatButtonStyle,
  IconButtonStyle,
  FormButtonStyle,
  StyledAddButton,
  StyledRemoveButton,
  SVGButton as SVGButtonStyle,
  BUTTON_INTENT,
  StyledButtonProps,
} from 'styles/buttons';
import colors from 'styles/colors';

// CONSTANTS
import { LOADING } from 'styles/commonClasses';

const WithButton = (WrappedButton: React.ElementType<React.ComponentPropsWithRef<'button'> & StyledButtonProps>) => {
  const ButtonWrapper = (
    props: React.ComponentPropsWithoutRef<'button'> & {
      icon?: React.ReactNode;
      intent?: BUTTON_INTENT;
      loading?: boolean;
      mobile?: boolean;
      stretch?: boolean;
      iconOnly?: boolean;
    }
  ) => {
    const {
      children,
      disabled = false,
      icon,
      intent,
      loading = false,
      mobile = false,
      onClick,
      stretch,
      type = 'button',
      ...rest
    } = props;

    let { className = '' } = rest;

    const appendClass = (newClass) => `${className} ${newClass}`;

    if (loading) {
      className = appendClass(LOADING);
    }

    return (
      <WrappedButton
        {...rest}
        className={className}
        onClick={onClick}
        disabled={disabled || loading}
        type={type}
        mobile={mobile}
        hasIcon={Boolean(props.icon)}
        intent={intent}
        stretch={stretch}
      >
        <div className="button-children">
          {' '}
          {/* used for proper visibility switching between this and loading state */}
          {icon}
          {children && <div>{children}</div>}
        </div>
        {loading && <Loading />}
      </WrappedButton>
    );
  };

  return ButtonWrapper;
};

// Base Buttons
export const AddButton = (props) =>
  WithButton(StyledAddButton)({ icon: <PlusIcon color={colors.fleetBlue} />, ...props });
export const DangerButton = WithButton(DangerButtonStyle);
export const DisabledButton = WithButton(DisabledButtonStyle);
export const FlatButton = WithButton(FlatButtonStyle);
export const FormButton = WithButton(FormButtonStyle);
export const IconButton = WithButton(IconButtonStyle);
export const LightDangerButton = WithButton(LightDangerButtonStyle);
export const LightTableButton = WithButton(LightTableButtonStyle);
export const LinkButton = WithButton(LinkButtonStyle);
export const PrimaryButton = WithButton(PrimaryButtonStyle);
export const PrimaryTableButton = WithButton(PrimaryTableButtonStyle);
export const RemoveButton = WithButton(StyledRemoveButton);
export const SecondaryButton = WithButton(SecondaryButtonStyle);
export const SuccessButton = WithButton(SuccessButtonStyle);
export const SVGButton = WithButton(SVGButtonStyle);

// Named Buttons
const createNamedButton = (buttonStyle, children, mixpanelEventName) => {
  let mixpanelTracking = () => null;
  if (mixpanelEventName) {
    mixpanelTracking = () => mixpanel.track(mixpanelEventName);
  }

  return (props) =>
    WithButton(buttonStyle)({
      children,
      ...props,
      onClick: (e) => {
        mixpanelTracking();
        if (typeof props.onClick === 'function') {
          props.onClick(e);
        }
      },
    });
};

export const CancelButton = createNamedButton(SecondaryButtonStyle, <FormattedMessage id="buttons.cancel" />, 'Cancel');
export const BackButton = createNamedButton(SecondaryButtonStyle, <FormattedMessage id="buttons.back" />, 'Back');
export const ConfirmButton = createNamedButton(
  SuccessButtonStyle,
  <FormattedMessage id="buttons.confirm" />,
  'Confirm'
);
export const SaveButton = createNamedButton(SuccessButtonStyle, <FormattedMessage id="buttons.save" />, 'Save');
